import { useState, useRef, useCallback, useEffect } from "react";
import html2canvas from "html2canvas";
const CHUNK_INTERVAL_MS = 30_000;

export const useCodingRecording = ({
  interviewId,
  containerRef,
  sessionId,
  questionId,
  socket,
  meetingId,

  getCode,
  getLanguage,
  startingChunkIndex = 0,
}) => {
 
  const socketRef = useRef(socket);
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [error, setError] = useState(null);
  const audioRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStartedRef = useRef(false);
  const videoStreamRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const recordingCanvasRef = useRef(null);
  const renderLoopRef = useRef(false);
  const chunkIndexRef = useRef(0);
  const isFinalizingRef = useRef(false);
  const pendingIsLastRef = useRef(false);
  const chunkTimerRef = useRef(null);
  const lastChunkPromiseRef = useRef(Promise.resolve());
  const finalChunkResolveRef = useRef(null);
  const finalChunkPromiseRef = useRef(Promise.resolve());
  const pendingChunkBlobRef = useRef(null);
  const lastVideoChunkRef = useRef("");

  const arrayBufferToBase64 = (buffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000; // 32KB
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(
        null,
        bytes.subarray(i, i + chunkSize),
      );
    }
    return btoa(binary);
  };

  // ── MIME type ─────────────────────────────────────────────────────────────
  const getSupportedMimeType = () => {
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    return (
      candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "video/webm"
    );
  };

  const isDrawingRef = useRef(false);

  const drawFrame = useCallback(async () => {
    const container = containerRef?.current;
    const canvas = recordingCanvasRef.current;
    if (!container || !canvas || isDrawingRef.current) return;

    isDrawingRef.current = true;
    try {
      const captured = await html2canvas(container, {
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      const ctx = canvas.getContext("2d");
      ctx.drawImage(captured, 0, 0, canvas.width, canvas.height);
    } catch (err) {
      console.error("[CodingRec] html2canvas error:", err);
    } finally {
      isDrawingRef.current = false;
    }
  }, [containerRef]);

  const RENDER_LOOP_INTERVAL_MS = 250; // ~4fps for code editor video

  const startRenderLoop = useCallback(() => {
    renderLoopRef.current = true;
    const loop = async () => {
      if (!renderLoopRef.current) return;
      await drawFrame();
      if (renderLoopRef.current) {
        setTimeout(loop, RENDER_LOOP_INTERVAL_MS);
      }
    };
    loop();
  }, [drawFrame]);

  const streamChunkOverSocket = useCallback(
    async (blob, isLast) => {
      const activeSocket = socketRef.current;
      if (!activeSocket) return;
      try {
        const videoBuffer = await blob.arrayBuffer();
        const chunkIndex = chunkIndexRef.current++;
        const code = getCode ? getCode() : "";
        const language = getLanguage ? getLanguage() : "";
        const video = arrayBufferToBase64(videoBuffer);

        if (isLast) {
          lastVideoChunkRef.current = video;
        }

       
        activeSocket.emit("code-video", {
          sessionId,
          meetingId,
          language,
          code,
          chunkIndex,
          video,
          timestamp: Date.now(),
        });
        // console.log(
        //   `[WBRec] Streamed chunk ${chunkIndex}${isLast ? " (final)" : ""} ` +
        //     `over socket (${videoBuffer.byteLength} bytes)`,
        // );
      } catch (err) {
        console.error("[WBRec] Failed to stream chunk over socket:", err);
      } finally {
        if (isLast) finalChunkResolveRef.current?.();
      }
    },
    
    [sessionId, meetingId, getCode, getLanguage],
  );
  const startNextChunkRecorder = useCallback(() => {
    const stream = videoStreamRef.current;
    if (!stream || isFinalizingRef.current) return;

    const mimeType = getSupportedMimeType();
    const recorder = new MediaRecorder(stream, { mimeType });

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        pendingChunkBlobRef.current = e.data;
      }
    };

    recorder.onstop = async () => {
      const blob = pendingChunkBlobRef.current;
      pendingChunkBlobRef.current = null;
      const isLast = pendingIsLastRef.current;

      if (blob) {
        // Wait for this chunk to actually finish sending before kicking
        // off the next recorder — each chunk is a fresh recording that
        // only starts once the previous one is off the wire.
        lastChunkPromiseRef.current = streamChunkOverSocket(blob, isLast);
        await lastChunkPromiseRef.current;
      }

      if (!isFinalizingRef.current) {
        startNextChunkRecorder();
      }
    };

    recorder.onerror = (e) => console.error("[WBRec] Recorder error:", e);

    videoRecorderRef.current = recorder;
    recorder.start(); // no timeslice — this single instance IS one whole chunk

    clearTimeout(chunkTimerRef.current);
    chunkTimerRef.current = setTimeout(() => {
      // Scheduled end of this chunk. onstop (above) starts the next one,
      // so this just closes the current chunk out on time.
      if (recorder.state !== "inactive") recorder.stop();
    }, CHUNK_INTERVAL_MS);
  }, [streamChunkOverSocket]);

  const startVideoChunkRecording = useCallback(
    async (audioTrack) => {
      const container = containerRef?.current;
      if (!container) {
        console.warn("[WBRec] No container ref — video recording skipped");
        return;
      }

      try {
        const canvas = document.createElement("canvas");
        canvas.width = container.offsetWidth || 1280;
        canvas.height = container.offsetHeight || 720;
        recordingCanvasRef.current = canvas;

        await drawFrame();
        startRenderLoop();

        const stream = canvas.captureStream(30);
        if (audioTrack) {
          stream.addTrack(audioTrack);
          console.log("[WBRec] Audio track added");
        }
        videoStreamRef.current = stream;

        // Resume the chunk sequence from startingChunkIndex on reconnect
        // (coding_video_index from the reconnect check) instead of always
        // restarting at 0, so we don't overwrite earlier chunks.
        chunkIndexRef.current = startingChunkIndex;
        isFinalizingRef.current = false;
        pendingIsLastRef.current = false;
        pendingChunkBlobRef.current = null;
        lastVideoChunkRef.current = "";
        lastChunkPromiseRef.current = Promise.resolve();
        finalChunkPromiseRef.current = new Promise((res) => {
          finalChunkResolveRef.current = res;
        });

        startNextChunkRecorder();
        console.log(
          `[WBRec] Video recording started (per-chunk recorders), ` +
            `starting at chunk index ${startingChunkIndex}`,
        );
      } catch (err) {
        console.error("[WBRec] Failed to start video:", err);
        // non-fatal — audio continues
      }
    },
    [
      containerRef,
      drawFrame,
      startRenderLoop,
      startNextChunkRecorder,
      startingChunkIndex,
    ],
  );
  const stopVideoRecording = useCallback(() => {
    return new Promise((resolve) => {
      renderLoopRef.current = false;
      clearTimeout(chunkTimerRef.current);
      isFinalizingRef.current = true;
      pendingIsLastRef.current = true;

      const recorder = videoRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        // Nothing active to flush — there's no final chunk to wait for.
        finalChunkResolveRef.current?.();
        resolve();
        return;
      }

      recorder.addEventListener("stop", resolve, { once: true });
      recorder.stop();
    });
  }, []);
  const startCodingRecording = useCallback(async () => {
    if (recordingStartedRef.current) {
      console.warn("[CodingRec] Already recording — ignoring duplicate start");
      return;
    }
    setError(null);
    setRecordingStatus("idle");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onerror = () => {
        setRecordingStatus("error");
        setError("Audio recording error.");
      };

      audioRecorderRef.current = recorder;
      recordingStartedRef.current = true;
      recorder.start(1000);
      setRecordingStatus("recording");
      console.log("[CodingRec] Audio recording started");

      const audioTrack = stream.getAudioTracks()[0] ?? null;
      await startVideoChunkRecording(audioTrack);
    } catch (err) {
      console.error("[CodingRec] Failed to start:", err);
      setRecordingStatus("error");
      setError(err.message || "Failed to start recording.");
      audioStreamRef.current?.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
  }, [startVideoChunkRecording]);

  const flushAndStopAudioRecorder = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = audioRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        resolve();
        return;
      }
      recorder.addEventListener("stop", resolve, { once: true });
      recorder.stop();
    });
  }, []);

  const cleanupStream = useCallback(() => {
    clearTimeout(chunkTimerRef.current);
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = null;
    videoStreamRef.current?.getTracks().forEach((t) => t.stop());
    videoStreamRef.current = null;
    audioRecorderRef.current = null;
    videoRecorderRef.current = null;
    recordingCanvasRef.current = null;
    recordingStartedRef.current = false;
    isFinalizingRef.current = false;
    pendingIsLastRef.current = false;
    pendingChunkBlobRef.current = null;
    lastVideoChunkRef.current = "";
  }, []);

  const stopAndSubmitCoding = useCallback(async () => {
    if (!recordingStartedRef.current) {
      console.warn("[WBRec] No active recording to submit");
      return;
    }
    setRecordingStatus("submitting");

    try {
      await flushAndStopAudioRecorder();
      await stopVideoRecording(); // ends + flags the final chunk

      // stopVideoRecording resolving only means the browser finished the
      // recorder itself — the async socket send for that final chunk
      // (base64 encode + emit) may still be in flight, so wait for both
      // promises before tearing the stream down.
      await lastChunkPromiseRef.current;
      await finalChunkPromiseRef.current;

      console.log(
        `[WBRec] Submit complete — ${chunkIndexRef.current} chunk(s) streamed, ` +
          "final chunk confirmed sent. Nothing to merge client-side.",
      );

      setRecordingStatus("done");
      return {
        chunkIndex: chunkIndexRef.current,
        lastVideoChunk: lastVideoChunkRef.current,
      };
    } catch (err) {
      console.error("[WBRec] Submit failed:", err);
      setRecordingStatus("error");
      setError(err.message || "Failed to submit recording.");
      throw err;
    } finally {
      cleanupStream();
    }
  }, [flushAndStopAudioRecorder, stopVideoRecording, cleanupStream]);

  // ─── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      renderLoopRef.current = false;
      clearTimeout(chunkTimerRef.current);
      isFinalizingRef.current = true;
      if (recordingStartedRef.current) {
        flushAndStopAudioRecorder().finally(cleanupStream);
        stopVideoRecording();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    recordingStatus,
    error,
    startCodingRecording,
    stopAndSubmitCoding,
    isRecording: recordingStatus === "recording",
    isSubmitting: recordingStatus === "submitting",
  };
};
